import { useInsiderMembershipInformation } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderMembershipInformation";
import { intlDate } from "@faire/web--source/common/localization/date-fns/intlDate";
import { getCurrentPath } from "@faire/web--source/common/Path";
import { removeLocaleFromUrl } from "@faire/web--source/ui/routing/util";
import { IMembership } from "@faire/web-api--source/indigofair/data/IMembership";
import { IInsiderMembershipInformation } from "@faire/web-api--source/indigofair/insider/IInsiderMembershipInformation";
import { route as brandPage } from "@faire/web-api--source/routes/brand/brandToken";
import shoppingBag from "@faire/web-api--source/routes/cart";
import checkout from "@faire/web-api--source/routes/checkout";
import { route as productDescriptionPage } from "@faire/web-api--source/routes/product/productToken";
import insiderMembershipInfo from "@faire/web-api--source/routes/user/insider/info";
/**
 * A hook that provides state-related information for Insider Membership.
 *
 * This hook returns an object that provides the current state of the Insider Membership, as well as booleans indicating whether the membership is in a particular state.
 * It relies on the `useInsiderMembershipInformation` hook to fetch the relevant information.
 *
 * @returns {IUseInsiderStateResult} An object containing Insider Membership state-related information.
 */ export function useInsiderState() {
    return getInsiderState(useInsiderMembershipInformation().insiderMembershipInformation);
}
/**
 * A function that provides state-related information for Insider Membership.
 *
 * To call this using a hook clients should call `useInsiderState()`.  This function can also be called with the
 * `insiderMembershipInformation` provided to allow server-side fetching of the data and passing in the server-fetched data.
 *
 * @returns {IUseInsiderStateResult} An object containing Insider Membership state-related information.
 */ export function getInsiderState(insiderMembershipInformation, pathname) {
    var _insiderMembershipInformation_state;
    var insiderState = (_insiderMembershipInformation_state = insiderMembershipInformation === null || insiderMembershipInformation === void 0 ? void 0 : insiderMembershipInformation.state) !== null && _insiderMembershipInformation_state !== void 0 ? _insiderMembershipInformation_state : IInsiderMembershipInformation.State.STATE_UNKNOWN;
    var isInsiderEligible = insiderState === IInsiderMembershipInformation.State.ELIGIBLE || insiderState === IInsiderMembershipInformation.State.CANCELLED;
    var isInsiderTrial = insiderState === IInsiderMembershipInformation.State.TRIALING;
    var isInsiderUnpaid = insiderState === IInsiderMembershipInformation.State.UNPAID;
    var isInsiderAutoEnrollTrial = insiderState === IInsiderMembershipInformation.State.AUTO_ENROLLED_TRIAL;
    var isInsiderActive = insiderState === IInsiderMembershipInformation.State.ACTIVE || isInsiderTrial || isInsiderUnpaid || isInsiderAutoEnrollTrial;
    var isInsiderCancelled = insiderState === IInsiderMembershipInformation.State.CANCELLED;
    var isInsiderPaused = insiderState === IInsiderMembershipInformation.State.PAUSED;
    var isInsider = isInsiderEligible || isInsiderActive || isInsiderUnpaid || isInsiderCancelled || isInsiderPaused || isInsiderAutoEnrollTrial;
    var isInsiderWillPause = (insiderMembershipInformation === null || insiderMembershipInformation === void 0 ? void 0 : insiderMembershipInformation.action_at_period_end) === IMembership.ActionAtPeriodEnd.PAUSE && !!(insiderMembershipInformation === null || insiderMembershipInformation === void 0 ? void 0 : insiderMembershipInformation.paused_until);
    var isInsiderUpcomingPaused = isInsiderWillPause && !isInsiderPaused;
    var isInsiderPausedOrWillPause = isInsiderPaused || isInsiderWillPause;
    var _insiderMembershipInformation_membership_created_at;
    var insiderSinceYear = intlDate((_insiderMembershipInformation_membership_created_at = insiderMembershipInformation === null || insiderMembershipInformation === void 0 ? void 0 : insiderMembershipInformation.membership_created_at) !== null && _insiderMembershipInformation_membership_created_at !== void 0 ? _insiderMembershipInformation_membership_created_at : 0, "yyyy");
    var insiderMembershipStateEventPrefix = getInsiderMembershipStateEventPrefix(insiderState);
    var insiderModalSourcePageEventPrefix = getInsiderModalSourcePageEventPrefix(pathname);
    return {
        isInsiderUpcomingPaused: isInsiderUpcomingPaused,
        insiderState: insiderState,
        isInsider: isInsider,
        isInsiderEligible: isInsiderEligible,
        isInsiderTrial: isInsiderTrial,
        isInsiderActive: isInsiderActive,
        isInsiderUnpaid: isInsiderUnpaid,
        isInsiderCancelled: isInsiderCancelled,
        isInsiderPaused: isInsiderPaused,
        isInsiderAutoEnrollTrial: isInsiderAutoEnrollTrial,
        isInsiderPausedOrWillPause: isInsiderPausedOrWillPause,
        insiderMembershipStateEventPrefix: insiderMembershipStateEventPrefix,
        insiderModalSourcePageEventPrefix: insiderModalSourcePageEventPrefix,
        insiderSinceYear: insiderSinceYear
    };
}
var getInsiderMembershipStateEventPrefix = function(state) {
    switch(state){
        case IInsiderMembershipInformation.State.UNPAID:
            return "unpaid";
        case IInsiderMembershipInformation.State.CANCELLED:
            return "churned";
        default:
            return "trial";
    }
};
var getInsiderModalSourcePageEventPrefix = function(pathname) {
    var currentPath = pathname !== null && pathname !== void 0 ? pathname : getCurrentPath().pathname;
    if (currentPath) {
        switch(removeLocaleFromUrl(currentPath)){
            case insiderMembershipInfo():
                return IMembership.Channel.MARKETING_PAGE;
            case shoppingBag():
                return IMembership.Channel.SHOPPING_BAG;
            case brandPage:
                return IMembership.Channel.BRAND_PAGE;
            case productDescriptionPage:
                return IMembership.Channel.PDP;
            case checkout():
                return IMembership.Channel.CHECKOUT;
            default:
                return IMembership.Channel.CHANNEL_UNKNOWN;
        }
    }
    return IMembership.Channel.CHANNEL_UNKNOWN;
};
